import { useUnit } from 'effector-react';
import { set } from 'lodash';
import $$api from 'models/api';
import $$giftTrustPage from 'models/pages/gift-trust';
import { useEffect, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

import './AddGiftItemModal.scss';

import HText from 'components/HText';
import HookFormControl from 'components/HookFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import PopUpWrapper from 'components/StyledTemplates/PopUpWrapper';
import { Button, H1 } from 'components/StyledTemplates/StyledTemplates';

import GuideService from 'services/GuideService';

import useModal from 'hooks/useModal';
import useResources from 'hooks/useResources';

import warning_icon from 'assets/icons/warning_amber_24px_two_tone.svg';

import { type Answers } from 'types/zod/answers';

const AddGiftItemModal = () => {
    const [article, setArticle] = useState('');
    const { t } = useResources();

    const { state = {}, close } = useModal('ADD_GIFT_ITEM_MODAL', {
        onClose: () => {
            form.reset({ date: '', value: '', description: '' });
        },
    });

    const answers = useUnit($$giftTrustPage.$tabs.map((tab) => tab?.[state.qInstanceId]?.answers));
    const giftsNumber = useUnit($$giftTrustPage.$tabs.map((tab) => tab?.[state.qInstanceId]?.giftTrustItems?.length));

    const form = useForm();

    useEffect(() => {
        (async () => {
            if (!article) {
                setArticle((await GuideService.getFirstAvailableArticle(['gift_trust_new_gift'])) || '');
            }
        })();
    }, [article]);

    const save = async (data: Answers) => {
        await saveAnswer(data);
        close();
    };

    const generateLetter = async (data: Answers) => {
        await saveAnswer(data);
        generate();
        close();
    };

    const saveAnswer = async (data: Answers) => {
        const answer = {
            date: data.date,
            value: +data.value,
            description: data.description,
        };

        if (!answers?.hidden?.gifts?._loop) {
            set(answers, 'hidden.gifts._loop', []);
        }
        answers.hidden.gifts._loop.push({ gift: answer });
        await $$api.answers.putAnswersFx({ id: state.qInstanceId, answers });
        await $$api.answers.getAnswersFx({ id: state.qInstanceId });
    };

    const generate = () => {
        return $$giftTrustPage.generateGiftButtonClicked({
            qInstanceId: state.qInstanceId,
            giftTrustItemId: giftsNumber - 1,
        });
    };

    return (
        <PopUpWrapper name="ADD_GIFT_ITEM_MODAL" whiteCross size="lg">
            <CommunityTab
                title={
                    <H1 extra="font-size: 25px !important; margin: 0 !important;">
                        {t('giftTrustPage.addGiftItemModal.title')}
                    </H1>
                }
                extraBody="align-items: flex-start;"
            >
                <div className="add-gift-item-modal">
                    <div className="new-gift-guide">
                        <div className="title">
                            <img src={warning_icon} alt="warning" />
                            <span>{t('giftTrustPage.addGiftItemModal.guideTitle')}</span>
                        </div>
                        <HText>{article}</HText>
                    </div>

                    <FormProvider {...form}>
                        <Form
                            onSubmit={form.handleSubmit(generateLetter)}
                            style={{ width: '100%', padding: '0 30px 0 20px' }}
                        >
                            <Form.Row>
                                <Form.Group as={Col} md={6}>
                                    <HookFormControl.Date
                                        name="date"
                                        data-qaid="date"
                                        label={t('giftTrustPage.addGiftItemModal.date')}
                                        rules={{ required: t('giftTrustPage.addGiftItemModal.dateRequired') }}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} md={6}>
                                    <HookFormControl.Money
                                        name="value"
                                        data-qaid="value"
                                        label={t('giftTrustPage.addGiftItemModal.value')}
                                        rules={{ required: t('giftTrustPage.addGiftItemModal.valueRequired') }}
                                    />
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group as={Col}>
                                    <HookFormControl.Text
                                        name="description"
                                        data-qaid="description"
                                        label={t('giftTrustPage.addGiftItemModal.description')}
                                        maxLength={50}
                                        rules={{ required: t('giftTrustPage.addGiftItemModal.descriptionRequired') }}
                                    />
                                </Form.Group>
                            </Form.Row>

                            <div className="buttons">
                                <Button
                                    data-qaid="save_button"
                                    variant="light"
                                    onClick={async () => {
                                        await save(form.getValues());
                                    }}
                                >
                                    {t('giftTrustPage.addGiftItemModal.save')}
                                </Button>
                                <Button data-qaid="generate_letter_button" type="submit" variant="secondary">
                                    {t('giftTrustPage.addGiftItemModal.generateLetter')}
                                </Button>
                            </div>
                        </Form>
                    </FormProvider>
                </div>
            </CommunityTab>
        </PopUpWrapper>
    );
};

export default AddGiftItemModal;
